import {ChangeDetectionStrategy, Component, OnInit, inject} from '@angular/core';
import {ActivatedRoute, Router} from '@angular/router';
import {Localization} from '../../../../projects/common/src/lib/interface/localization.extension';
import {AccountApiService} from '../../common/services/api/routes/account-api.service';
import {AuthGuardService} from '../../common/services/auth-guard.service';
import {Logger} from '../../common/utils/logger';

type Phase = 'ask' | 'working' | 'done' | 'invalid' | 'error';

/**
 * /account/email-revert?token=…: "This wasn't me" from the notice sent to the
 * OLD address when someone asks to change the sign-in email.
 *
 * Open to anyone holding the link, because whoever made the change may also
 * have changed the password. Nothing happens until the button is pressed:
 * mail scanners open links, and an undo that ran on opening would fire on
 * every scan of the email.
 */
@Component({
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  selector: 'app-email-revert-page',
  templateUrl: 'email-revert.page.html',
})
export class EmailRevertPage extends Localization implements OnInit {
  private readonly route = inject(ActivatedRoute);
  private readonly router = inject(Router);
  private readonly accountApi = inject(AccountApiService);
  private readonly authGuard = inject(AuthGuardService);

  phase: Phase = 'ask';
  /** The address the account signs in with again. */
  email = '';
  restored = false;
  passwordReset = false;

  private token = '';

  ngOnInit() {
    // No auth guard runs here, and it is the guard that lifts the start-up
    // splash; left up, it hides the page.
    this.authGuard.loader.set(false);
    this.authGuard.loader$.next(false);
    this.token = this.route.snapshot.queryParamMap.get('token') || '';
    if (!this.token) {
      this.phase = 'invalid';
    }
  }

  undo() {
    if (!this.token || this.phase === 'working') {
      return;
    }
    this.phase = 'working';
    this.accountApi.revertEmailChange(this.token).then(result => {
      this.email = result?.email || '';
      this.restored = !!result?.restored;
      this.passwordReset = !!result?.passwordReset;
      this.phase = 'done';
    }).catch(err => {
      const code = err?.msg?.errorCode;
      Logger.warn('Email change undo failed', code);
      this.phase = code === 'email_revert_invalid' ? 'invalid' : 'error';
    });
  }

  /** Every session was ended, this browser's too: sign out cleanly, then in. */
  signIn() {
    this.router.navigateByUrl('/signout');
  }
}
